import { createCloudpackClient, getActiveSessionsPath } from '@ms-cloudpack/api-server';
import type { Session } from '@ms-cloudpack/common-types';
import { readJson } from '@ms-cloudpack/json-utilities';
import type { TaskReporter } from '@ms-cloudpack/task-reporter';
import { readdir, rm } from 'fs/promises';
import path from 'path';
import fs from 'fs';

/**
 * Get array of active sessions.
 * An active session is one that has a valid session file and the api server is still active.
 * This function will also delete inactive session files.
 */
export async function getActiveSessions(params: { cachePath: string; reporter?: TaskReporter }): Promise<Session[]> {
  const { reporter, cachePath } = params;
  const activeSessionPaths = getActiveSessionsPath(cachePath);
  if (!fs.existsSync(activeSessionPaths)) {
    return [];
  }
  const activeSessionFiles = await readdir(activeSessionPaths);
  const activeSessionsPromises = activeSessionFiles.map(async (activeSessionFile) => {
    const session = await readJson<Session>(path.join(activeSessionPaths, activeSessionFile));
    if (session && (await isSessionActive(session, reporter))) {
      return session;
    } else {
      console.debug(`Deleting invalid session file: ${activeSessionFile}`);
      await rm(path.join(activeSessionPaths, activeSessionFile));
      return null;
    }
  });

  const activeSessionsResults = await Promise.all(activeSessionsPromises);
  return activeSessionsResults.filter((session) => session !== null);
}

async function isSessionActive(session: Session, reporter?: TaskReporter): Promise<boolean> {
  const { apiServer } = session.urls;

  if (!apiServer) {
    return false;
  }

  console.debug(`Checking session: ${session.id}`);

  const client = await createCloudpackClient({ url: apiServer, reporter });
  // Ping the api server to see if it is still active
  let isActive = false;
  try {
    isActive = (await client.getSessionId.query()) === session.id;
  } catch {
    /* no-op */
  } finally {
    await client.close();
  }

  return isActive;
}
